import axios from 'axios';

const API_BASE_URL = process.env.NODE_ENV === "production"
  ? (process.env.REACT_APP_PROD_API_BASE_URL || "https://test.conversationaily.ai")
  : (process.env.REACT_APP_DEV_API_BASE_URL || "https://testone.conversationaily.ai");

/**
 * Save consultation session data when consultation is stopped
 * @param {Object} consultationData - All consultation data to be saved
 * @returns {Promise} - API response
 */
export const saveConsultationSession = async (consultationData, apiBaseUrl) => {
  try {
    const baseUrl = apiBaseUrl || API_BASE_URL;

    const userId = localStorage.getItem("userId");

    const payload = {
      ...consultationData,
      user_id: Number(userId)
    };

    const response = await axios.post(
      `${baseUrl}/api/user/consultation/stop`,
      payload
    );

    const data = response.data;

    const sessionId =
      data?.session_id ||
      data?.consultation_session_id ||
      null;

    if (sessionId) {
      window.dispatchEvent(
        new CustomEvent('consultation-session-saved', {
          detail: { sessionId: String(sessionId) }
        })
      );
    }

    return data;
  } catch (error) {
    console.error('❌ Error saving consultation session:', error);
    throw error;
  }
};

/**
 * Get consultation sessions for a doctor
 * @param {number} doctorId - Doctor ID (optional)
 * @param {number} limit - Number of sessions to retrieve
 * @returns {Promise} - API response with sessions
 */
export const getConsultationSessions = async (doctorId = null, limit = 50) => {
  try {
    const url = doctorId
      ? `${API_BASE_URL}/api/consultation/sessions/${doctorId}?limit=${limit}`
      : `${API_BASE_URL}/api/consultation/sessions?limit=${limit}`;

    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching consultation sessions:', error);
    throw error;
  }
};

/**
 * Format consultation data for saving
 * @param {Object} params - Consultation parameters
 * @returns {Object} - Formatted consultation data
 */
export const formatConsultationData = ({
  doctorId,
  patientId,
  speciality,
  caseType,
  empathySummary,
  matchedSuggestions,
  stigmatizingWords,
  transcript,
  duration
}) => {
  return {
    doctor_id: doctorId,
    patient_id: patientId,
    speciality: speciality || 'General',
    case_type: caseType || 'Standard',
    empathy_summary: empathySummary,
    matched_suggestions: matchedSuggestions || [],
    stigmatizing_words: stigmatizingWords || [],
    transcript: transcript || '',
    duration: duration || { hours: 0, minutes: 0, seconds: 0 }
  };
};

/**
 * Extract speciality from patient data
 * @param {Object} patient - Patient data object
 * @returns {string} - Speciality name
 */
export const extractSpeciality = (patient) => {
  return patient?.Category || 'General Medicine';
};

/**
 * Extract case type from patient data
 * @param {Object} patient - Patient data object
 * @returns {string} - Case type
 */
export const extractCaseType = (patient) => {
  return patient?.["Case Type"] || 'Standard Consultation';
};

/**
 * Update consultation session with analysis results
 * @param {number} sessionId - Session ID to update
 * @param {Object} analysisData - Analysis results
 * @returns {Promise} - API response
 */
export const updateConsultationSession = async (sessionId, analysisData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/consultation/update/${sessionId}`, analysisData);
    return response.data;
  } catch (error) {
    console.error('Error updating consultation session:', error);
    throw error;
  }
};